import i18n from '@/utils/i18n';
import React, { ReactNode, RefObject, useState } from 'react';
import { Badge, Typography, View, YStack } from '@usepepper/sprout-kit-ui';
import {
  createColumnHelper,
  flexRender,
  HeaderGroup,
  Row,
} from '@tanstack/react-table';
import {
  CustomColumnDef,
  TableScrollHandle,
} from '@experiences/components/table/types';
import {
  CUSTOMERS_TABLE_COLUMNS,
  CUSTOMERS_TABLE_ROW_HEIGHT,
} from '@finance-hub/domain/customers/components/customers-table-columns';
import {
  CustomerSort,
  CustomerSortField,
  FinanceHubCustomerRow,
} from '@finance-hub/domain/customers/blocs/types/customer-list';
import { CustomersTableRow } from '@finance-hub/domain/customers/components/customers-table-row';
import { FinanceHubTableTooltipTextCell } from '@finance-hub/components/finance-hub-table-tooltip-text-cell';
import { LayoutChangeEvent, Platform, ViewStyle } from 'react-native';
import { Table } from '@experiences/components/table/table';
import { TableHeaderCell } from '@experiences/components/table/table-header-cell';
import { TableHeaderRow } from '@experiences/components/table/table-header-row';
import { useTheme } from '@/contexts/theme-context';

const columnHelper = createColumnHelper<FinanceHubCustomerRow>();

const BODY_CONTAINER_STYLE = Platform.select<ViewStyle>({
  web: { width: '100%' },
});

const renderPresenceCell = (
  isPresent: boolean,
  missingColor: 'error' | 'warning',
) =>
  isPresent ? (
    <Badge color="success" size="$sm" variant="outline">
      {i18n.t('finance_hub.customers.table.on_file')}
    </Badge>
  ) : (
    <Badge color={missingColor} size="$sm" variant="outline">
      {i18n.t('finance_hub.customers.table.missing')}
    </Badge>
  );

const renderYesCell = (isEnabled: boolean) =>
  isEnabled ? (
    <Typography color="$textSuccess" fontWeight="$medium" size="$sm">
      {i18n.t('finance_hub.customers.table.yes')}
    </Typography>
  ) : (
    <Typography color="$textSecondary" size="$sm">
      —
    </Typography>
  );

const CELL_RENDERERS: Record<
  CustomerSortField,
  (row: FinanceHubCustomerRow) => ReactNode
> = {
  accountId: (row) => (
    <FinanceHubTableTooltipTextCell
      color="$textSecondary"
      size="$sm"
      text={row.accountIdLabel}
    />
  ),
  autopay: (row) => renderYesCell(row.hasAutopay),
  contactInfo: (row) => renderPresenceCell(row.hasContactInfo, 'error'),
  customerName: (row) => (
    <YStack gap="$2">
      <FinanceHubTableTooltipTextCell
        fontWeight="$medium"
        size="$sm"
        text={row.customerNameLabel}
      />
      {row.addressLabel ? (
        <FinanceHubTableTooltipTextCell
          color="$textSecondary"
          size="$xs"
          text={row.addressLabel}
        />
      ) : null}
    </YStack>
  ),
  invoiceCount: (row) => (
    <Typography color="$textSecondary" size="$sm">
      {row.invoiceCount}
    </Typography>
  ),
  outstanding: (row) => (
    <Typography bold size="$sm">
      {row.outstandingLabel}
    </Typography>
  ),
  paymentMethod: (row) => renderPresenceCell(row.hasPaymentMethod, 'warning'),
  paymentPlan: (row) => renderYesCell(row.hasPaymentPlan),
  statement: (row) => renderYesCell(row.hasStatement),
};

const COLUMN_DEFS: CustomColumnDef<FinanceHubCustomerRow>[] =
  CUSTOMERS_TABLE_COLUMNS.map((column) =>
    columnHelper.display({
      cell: (info) => CELL_RENDERERS[column.sortField](info.row.original),
      header: () => i18n.t(column.labelKey),
      id: column.id,
      meta: { initialPercentWidth: column.initialPercentWidth },
      minSize: column.minSize,
    }),
  );

const renderRow = ({ row }: { row: Row<FinanceHubCustomerRow> }) => (
  <CustomersTableRow key={row.original.chatId} row={row} />
);

interface Props {
  emptyComponent: ReactNode;
  listFooter?: ReactNode;
  /** Omitted while the complete list loads, which leaves headers inert. */
  onColumnPress?: (field: CustomerSortField) => void;
  rows: FinanceHubCustomerRow[];
  scrollHandleRef?: RefObject<TableScrollHandle | null>;
  sort: CustomerSort;
}

export const CustomersTable: React.FC<Props> = ({
  emptyComponent,
  listFooter,
  onColumnPress,
  rows,
  scrollHandleRef,
  sort,
}) => {
  const theme = useTheme();
  const [layoutWidth, setLayoutWidth] = useState<number | null>(null);

  const handleLayout = (event: LayoutChangeEvent) => {
    setLayoutWidth(event.nativeEvent.layout.width);
  };

  const renderHeaderRow = ({
    headerGroup,
  }: {
    headerGroup: HeaderGroup<FinanceHubCustomerRow>;
  }) => (
    <TableHeaderRow key={headerGroup.id}>
      {headerGroup.headers.map((header) => (
        <TableHeaderCell
          columnId={header.id}
          key={header.id}
          {...(onColumnPress
            ? {
                onPress: () => onColumnPress(header.id as CustomerSortField),
                sortDirection:
                  sort.field === header.id ? sort.direction : undefined,
              }
            : {})}
        >
          <Typography
            bold
            color="$textSecondary"
            size="$xs"
            textTransform="uppercase"
          >
            <>
              {flexRender(header.column.columnDef.header, header.getContext())}
            </>
          </Typography>
        </TableHeaderCell>
      ))}
    </TableHeaderRow>
  );

  if (!layoutWidth) {
    return (
      <View
        accessibilityRole="progressbar"
        accessibilityState={{ busy: true }}
        accessible
        flex={1}
        onLayout={handleLayout}
      />
    );
  }

  return (
    <View flex={1} onLayout={handleLayout}>
      <Table
        bodyContainerStyle={BODY_CONTAINER_STYLE}
        cellPadding={theme.sk.spacing.spaceSmall}
        columns={COLUMN_DEFS}
        data={rows}
        emptyComponent={emptyComponent}
        estimatedRowHeight={CUSTOMERS_TABLE_ROW_HEIGHT}
        headerPadding={theme.sk.spacing.spaceSmall}
        isActive
        layoutWidth={layoutWidth}
        listFooter={listFooter}
        renderHeaderRow={renderHeaderRow}
        renderRow={renderRow}
        scrollHandleRef={scrollHandleRef}
      />
    </View>
  );
};
